import { useEffect, useMemo, useRef, useState } from "react";
import { EventLayout } from "@/components/EventLayout";
import { Gate } from "@/components/FeatureGate";
import { useEvent } from "@/context/EventContext";
import { formatApiErrorDetail } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Briefcase, FileCheck, Link2, Upload, FileText, Download, CalendarClock, BellRing } from "lucide-react";

const empty = { name: "", category: "Venue", contact_email: "", contact_phone: "", status: "lead", quote_amount: 0, contract_status: "none", budget_item_id: null, payment_plan: [], notes: "" };
const CATEGORIES = ["Venue", "Catering", "Photography", "Florals", "Music", "Attire", "Stationery", "Décor", "Transport", "Cake", "Other"];
const statusCls = {
  lead: "bg-stone-100 text-stone-500",
  booked: "bg-[#D4C5B3]/30 text-[#8a744f]",
  confirmed: "bg-[#8C9A86]/15 text-[#5f6b59]",
};
const contractCls = {
  none: "text-stone-400",
  sent: "text-[#8a744f]",
  signed: "text-[#5f6b59]",
};
const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const planPaid = (v) => (v.payment_plan || []).filter((p) => p.paid).reduce((s, p) => s + Number(p.amount || 0), 0);

export default function Vendors() {
  const { eapi } = useEvent();
  const [vendors, setVendors] = useState([]);
  const [budget, setBudget] = useState([]);
  const [modal, setModal] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const [v, b] = await Promise.all([eapi.get("/vendors"), eapi.get("/budget")]);
    setVendors(v.data); setBudget(b.data);
  };

  const sendTestReminder = async () => {
    setTesting(true);
    try {
      const { data } = await eapi.post("/vendors/test-reminder", {});
      toast.success(data.message || "Test reminder sent to your email.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    quoted: vendors.reduce((s, v) => s + Number(v.quote_amount || 0), 0),
    booked: vendors.filter((v) => v.status !== "lead").length,
    signed: vendors.filter((v) => v.contract_status === "signed").length,
  }), [vendors]);

  const budgetName = (id) => budget.find((b) => b.id === id)?.name;

  const save = async (form) => {
    try {
      if (form.id) await eapi.put(`/vendors/${form.id}`, form);
      else await eapi.post("/vendors", form);
      toast.success("Vendor saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await eapi.delete(`/vendors/${id}`); toast.success("Vendor removed"); load(); };

  const uploadDoc = async (vendorId, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("This file is too large. The maximum size is 10 MB."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await eapi.post(`/vendors/${vendorId}/documents`, fd);
      toast.success("Document uploaded"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openDoc = async (vendorId, doc) => {
    try {
      const res = await eapi.get(`/vendors/${vendorId}/documents/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };
  const removeDoc = async (vendorId, docId) => {
    try { await eapi.delete(`/vendors/${vendorId}/documents/${docId}`); toast.success("Document removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <EventLayout>
      <Gate featureKey="vendors">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline">Vendors</p>
            <h1 className="font-serif text-4xl tracking-tight">Your suppliers</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={sendTestReminder} disabled={testing} data-testid="test-reminder-btn"
              className="flex items-center gap-2 rounded-full border border-[#E8E6E1] text-stone-600 px-5 py-2.5 text-sm hover:bg-stone-50 transition-colors disabled:opacity-50">
              <BellRing size={15} /> {testing ? "Sending…" : "Send test reminder"}
            </button>
            <button onClick={() => setModal({ ...empty })} data-testid="add-vendor-btn"
              className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors">
              <Plus size={16} /> Add vendor
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Tile icon={Briefcase} label="Total quoted" value={money(totals.quoted)} accent testid="vendor-quoted" />
          <Tile icon={Briefcase} label="Booked" value={totals.booked} testid="vendor-booked" />
          <Tile icon={FileCheck} label="Contracts signed" value={totals.signed} testid="vendor-signed" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {vendors.length === 0 && <p className="sm:col-span-2 bg-white border border-dashed border-[#E8E6E1] rounded-lg p-10 text-center text-sm text-stone-400">No vendors yet.</p>}
          {vendors.map((v) => (
            <VendorCard key={v.id} v={v} budgetName={budgetName} onEdit={() => setModal({ ...empty, ...v, payment_plan: v.payment_plan || [] })}
              onRemove={() => remove(v.id)} onUpload={uploadDoc} onOpenDoc={openDoc} onRemoveDoc={removeDoc} />
          ))}
        </div>
      </div>

      {modal && <VendorModal initial={modal} budget={budget} onClose={() => setModal(null)} onSave={save} />}
      </Gate>
    </EventLayout>
  );
}

function VendorCard({ v, budgetName, onEdit, onRemove, onUpload, onOpenDoc, onRemoveDoc }) {
  const fileRef = useRef(null);
  const docs = v.documents || [];
  const plan = v.payment_plan || [];
  const paid = planPaid(v);
  return (
    <div data-testid={`vendor-card-${v.id}`}
      className="bg-white border border-[#E8E6E1] rounded-lg p-5 transition-transform duration-300 hover:-translate-y-0.5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="overline">{v.category}</p>
          <p className="font-serif text-2xl tracking-tight mt-1">{v.name}</p>
          {v.contact_email && <p className="text-xs text-stone-400 mt-1 truncate">{v.contact_email}</p>}
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} data-testid={`edit-vendor-${v.id}`} className="p-2 text-stone-400 hover:text-stone-700"><Pencil size={15} /></button>
          <button onClick={onRemove} data-testid={`delete-vendor-${v.id}`} className="p-2 text-stone-400 hover:text-[#C67A7A]"><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusCls[v.status]}`}>{v.status}</span>
        <span className={`text-xs flex items-center gap-1 ${contractCls[v.contract_status]}`}><FileCheck size={12} /> Contract {v.contract_status}</span>
        <span className="text-sm font-medium text-stone-700 ml-auto">{money(v.quote_amount)}</span>
      </div>

      {Number(v.quote_amount) > 0 && (
        <div className="mt-3" data-testid={`vendor-balance-${v.id}`}>
          <div className="flex items-center justify-between text-[11px] text-stone-500 mb-1">
            <span>{money(paid)} paid</span>
            <span>{money(Math.max(0, v.quote_amount - paid))} still owed</span>
          </div>
          <div className="h-2 rounded-full bg-[#F0EEE9] overflow-hidden">
            <div className="h-full rounded-full bg-[#8C9A86] transition-all duration-500" style={{ width: `${Math.min(100, (paid / v.quote_amount) * 100)}%` }} />
          </div>
        </div>
      )}

      {plan.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#F0EEE9]" data-testid={`vendor-plan-${v.id}`}>
          <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 flex items-center gap-1.5"><CalendarClock size={12} /> Payment plan</p>
          <div className="space-y-1.5">
            {plan.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-xs">
                <span className="text-stone-600 truncate">{p.label}{p.due_date ? ` · ${p.due_date}` : ""}</span>
                <span className={`ml-2 shrink-0 ${p.paid ? "text-[#5f6b59]" : "text-stone-500"}`}>{money(p.amount)}{p.paid ? " ✓" : ""}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-400 mt-2">{money(paid)} paid of {money(v.quote_amount)}</p>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[#F0EEE9]">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1.5"><FileText size={12} /> Documents</p>
          <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
            data-testid={`vendor-doc-input-${v.id}`}
            onChange={(e) => { onUpload(v.id, e.target.files); e.target.value = ""; }} />
          <button onClick={() => fileRef.current?.click()} data-testid={`vendor-doc-upload-${v.id}`}
            className="flex items-center gap-1.5 text-xs text-[#5f6b59] hover:text-[#2C2A28]"><Upload size={13} /> Upload</button>
        </div>
        {docs.length === 0 ? (
          <p className="text-xs text-stone-400">Invoices & contracts (PDF, JPG, PNG, DOCX, XLSX · max 10 MB).</p>
        ) : (
          <div className="space-y-1.5">
            {docs.map((d) => (
              <div key={d.id} data-testid={`vendor-doc-${d.id}`} className="flex items-center gap-2 text-xs">
                <button onClick={() => onOpenDoc(v.id, d)} className="flex items-center gap-1.5 text-stone-600 hover:text-[#5f6b59] min-w-0">
                  <Download size={12} className="shrink-0" /><span className="truncate">{d.filename}</span>
                </button>
                <button onClick={() => onRemoveDoc(v.id, d.id)} data-testid={`vendor-doc-remove-${d.id}`} className="ml-auto p-1 text-stone-300 hover:text-[#C67A7A]"><X size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {v.budget_item_id && budgetName(v.budget_item_id) && (
        <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5"><Link2 size={12} /> Linked to budget: {budgetName(v.budget_item_id)}</p>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border p-5 ${accent ? "bg-[#8C9A86] border-[#8C9A86] text-white" : "bg-white border-[#E8E6E1]"}`}>
      <Icon size={17} className={accent ? "text-white/80" : "text-[#8C9A86]"} />
      <p className="font-serif text-3xl mt-3">{value}</p>
      <p className={`text-xs mt-1 ${accent ? "text-white/80" : "text-stone-500"}`}>{label}</p>
    </div>
  );
}

function VendorModal({ initial, budget, onClose, onSave }) {
  const [form, setForm] = useState({ ...initial, payment_plan: initial.payment_plan || [] });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const plan = form.payment_plan || [];
  const allocated = plan.reduce((s, p) => s + Number(p.amount || 0), 0);
  const quote = Number(form.quote_amount || 0);
  const balanced = plan.length === 0 || (quote > 0 && Math.abs(allocated - quote) < 0.01);

  const setPlan = (next) => set("payment_plan", next);
  const addRow = () => setPlan([...plan, { label: "", amount: 0, due_date: "" }]);
  const updRow = (i, k, val) => setPlan(plan.map((p, idx) => idx === i ? { ...p, [k]: val } : p));
  const delRow = (i) => setPlan(plan.filter((_, idx) => idx !== i));

  const submit = () => {
    if (plan.length > 0 && quote <= 0) { toast.error("Set a quote amount before adding a payment plan."); return; }
    if (!balanced) { toast.error(`Payment plan must total ${money(quote)} (currently ${money(allocated)}).`); return; }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6 fade-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vendor-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">{form.id ? "Edit vendor" : "Add vendor"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <F label="Vendor name" value={form.name} onChange={(v) => set("name", v)} testid="vm-name" />
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Category" value={form.category} onChange={(v) => set("category", v)} options={CATEGORIES} testid="vm-category" />
            <label className="block">
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Quote amount</span>
              <MoneyInput value={form.quote_amount} onChange={(v) => set("quote_amount", v === "" ? 0 : v)} testid="vm-quote" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Contact email" value={form.contact_email} onChange={(v) => set("contact_email", v)} testid="vm-email" />
            <F label="Contact phone" value={form.contact_phone} onChange={(v) => set("contact_phone", v)} testid="vm-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Sel label="Status" value={form.status} onChange={(v) => set("status", v)} options={["lead", "booked", "confirmed"]} testid="vm-status" />
            <Sel label="Contract" value={form.contract_status} onChange={(v) => set("contract_status", v)} options={["none", "sent", "signed"]} testid="vm-contract" />
          </div>
          <div>
            <span className="text-xs font-medium text-stone-500 mb-1.5 block">Link to budget item</span>
            <select value={form.budget_item_id || ""} onChange={(e) => set("budget_item_id", e.target.value || null)} data-testid="vm-budget"
              className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#8C9A86]">
              <option value="">None</option>
              {budget.map((b) => <option key={b.id} value={b.id}>{`${b.name} (${b.category})`}</option>)}
            </select>
          </div>

          <div className="pt-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-stone-500">Payment plan</span>
              <button onClick={addRow} data-testid="vm-add-installment" className="flex items-center gap-1 text-xs text-[#5f6b59] hover:text-[#2C2A28]"><Plus size={13} /> Add payment</button>
            </div>
            {plan.length === 0 && <p className="text-xs text-stone-400 mb-2">Optional. Split the quote into scheduled payments that must add up to the quote.</p>}
            <div className="space-y-2">
              {plan.map((p, i) => (
                <div key={p.id || i} data-testid={`vm-installment-${i}`} className="grid grid-cols-[1fr_84px_120px_auto] gap-2 items-center">
                  <input value={p.label || ""} placeholder="e.g. Deposit" onChange={(e) => updRow(i, "label", e.target.value)} data-testid={`vm-inst-label-${i}`}
                    className="rounded-md border border-[#E8E6E1] px-2.5 py-2 text-sm outline-none focus:border-[#8C9A86]" />
                  <MoneyInput value={p.amount} onChange={(v) => updRow(i, "amount", v === "" ? 0 : v)} testid={`vm-inst-amount-${i}`}
                    inputClassName="w-full rounded-md border border-[#E8E6E1] bg-white pl-6 pr-2 py-2 text-sm outline-none focus:border-[#8C9A86]" />
                  <input type="date" value={p.due_date || ""} onChange={(e) => updRow(i, "due_date", e.target.value)} data-testid={`vm-inst-date-${i}`}
                    className="rounded-md border border-[#E8E6E1] px-2 py-2 text-sm outline-none focus:border-[#8C9A86]" />
                  <button onClick={() => delRow(i)} data-testid={`vm-inst-remove-${i}`} className="p-1.5 text-stone-300 hover:text-[#C67A7A]"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            {plan.length > 0 && (
              <p className={`text-xs mt-2 ${balanced ? "text-[#5f6b59]" : "text-[#C67A7A]"}`} data-testid="vm-plan-balance">
                {balanced ? `Balanced · ${money(allocated)} of ${money(quote)}` : `Allocated ${money(allocated)} of ${money(quote)}. Needs to total ${money(quote)}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#E8E6E1] py-2.5 text-sm hover:bg-stone-50">Cancel</button>
          <button onClick={submit} data-testid="vm-save" disabled={!balanced}
            className="flex-1 rounded-full bg-[#2C2A28] text-white py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Save</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
function Sel({ label, value, onChange, options, testid }) {
  return (
    <div>
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#8C9A86] capitalize">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
